import { registerSW } from "virtual:pwa-register";
import { Capacitor } from "@capacitor/core";
import { App as NativeApp } from "@capacitor/app";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { expeditions as baseExpeditions, getExpeditionForLevel, halls, levels as baseLevels, localizeExpedition, localizedLevels } from "./content.js";
import { applyStaticTranslations, getLanguage, locale, setLanguage, t } from "./i18n.js";
import { renderMapSvg } from "./atlas.js";
import { renderShareCard } from "./share.js";
import { LAYER_LIMIT, LAYER_STEP, applyEvent, blankIndex, hintTarget, initialState, pieceTotal, restorationMask, restoredCount, solved as isSolved } from "./engine.js";

let lang = getLanguage();
let levels = localizedLevels(lang);
let expeditions = baseExpeditions.map(item => localizeExpedition(item, lang));

const DAILY_EPOCH = Date.UTC(2026, 8, 1);
const SITE_URL = "https://lostartatlas.com/";
const FREE_LEVELS = 6; // expeditions 01–02 are free; the one-time Full Museum unlock opens the rest

const state = {
  level: 0,
  completed: Math.min(baseLevels.length, Number(localStorage.getItem("atlas-completed") || 0)),
  hints: Number(localStorage.getItem("atlas-hints") || 12),
  muted: localStorage.getItem("atlas-muted") === "true",
  values: [],
  history: [],
  selected: null,
  actions: 0,
  hintsUsed: 0,
  startedAt: 0,
  solvedMs: 0,
  restoredMask: [],
  settledAt: [],
  mode: "campaign",
  events: [],
  attemptId: null,
  challengeSalt: null,
  solved: false,
  atlasExpedition: null,
  hall: halls[0]
};

const online = {
  available: false,
  user: null,
  guest: null,
  pass: { permanent: localStorage.getItem("atlas-full-access") === "true", active: localStorage.getItem("atlas-full-access") === "true" || (localStorage.getItem("atlas-pass-until") ? new Date(localStorage.getItem("atlas-pass-until")) > new Date() : false), until: localStorage.getItem("atlas-pass-until") || null },
  challenge: null,
  streak: null,
  leaderboard: [],
  lastResult: null
};

const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
const intro = $("#intro");
const atlas = $("#atlas");
const game = $("#game");
const board = $("#puzzle-board");
const reveal = $("#reveal");
const modalBackdrop = $("#modal-backdrop");
const collectionModal = $("#collection-modal");
const arenaModal = $("#arena-modal");
const passModal = $("#pass-modal");
let audioContext;
let toastTimer;
let progressTimer;
const isNative = Capacitor.isNativePlatform();
const ANALYTICS_CONSENT_KEY = "atlas-analytics-consent";
const ANALYTICS_ID_KEY = "atlas-analytics-id";

function analyticsConsent() { return localStorage.getItem(ANALYTICS_CONSENT_KEY); }
function analyticsId() {
  let id = localStorage.getItem(ANALYTICS_ID_KEY);
  if (!id) { id = crypto.randomUUID(); localStorage.setItem(ANALYTICS_ID_KEY, id); }
  return id;
}
function track(event, properties = {}) {
  if (analyticsConsent() !== "accepted") return;
  api("/analytics/events", { method: "POST", keepalive: true, body: JSON.stringify({ consent: true, anonymousId: analyticsId(), event, properties: { source: isNative ? Capacitor.getPlatform() : "web", ...properties } }) }).catch(() => {});
}
function renderConsent(force = false) {
  $("#consent-banner").hidden = !force && analyticsConsent() !== null;
}
function chooseAnalytics(value) {
  const previousId = localStorage.getItem(ANALYTICS_ID_KEY);
  localStorage.setItem(ANALYTICS_CONSENT_KEY, value);
  $("#consent-banner").hidden = true;
  if (value === "accepted") track("app_open");
  else if (previousId) {
    api("/analytics/events", { method: "DELETE", body: JSON.stringify({ anonymousId: previousId }) }).catch(() => {});
    localStorage.removeItem(ANALYTICS_ID_KEY);
  }
}

const pad2 = value => String(value).padStart(2, "0");
const formatNumber = value => Number(value).toLocaleString(locale());
const formatTime = ms => {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${pad2(total % 60)}`;
};
const hintsLabel = count => (count === 0 ? t("reveal.noHints") : count === 1 ? t("reveal.hintsOne") : t("reveal.hints", { count }));
const expeditionIndexes = expeditionId => levels.map((level, index) => ({ level, index })).filter(item => item.level.expedition === expeditionId);
const dailyNumber = day => Math.max(1, Math.round((Date.UTC(...day.split("-").map((part, i) => Number(part) - (i === 1 ? 1 : 0))) - DAILY_EPOCH) / 86400000) + 1);
const current = () => levels[state.level];
const hasPass = () => online.pass?.active === true;
const levelLocked = index => index >= FREE_LEVELS && !hasPass();

async function hapticTap(style = ImpactStyle.Light) {
  if (!isNative) return;
  try { await Haptics.impact({ style }); } catch (_) {}
}

async function hapticSuccess() {
  if (!isNative) return;
  try { await Haptics.notification({ type: NotificationType.Success }); } catch (_) {}
}

function save() {
  localStorage.setItem("atlas-completed", String(state.completed));
  localStorage.setItem("atlas-hints", String(state.hints));
  localStorage.setItem("atlas-muted", String(state.muted));
  if (online.user) {
    clearTimeout(progressTimer);
    progressTimer = setTimeout(() => syncProgress(), 500);
  }
}

function setPass(pass) {
  online.pass = pass || { active: false, permanent: false, until: null };
  if (online.pass.permanent) localStorage.setItem("atlas-full-access", "true"); else localStorage.removeItem("atlas-full-access");
  if (online.pass.until) localStorage.setItem("atlas-pass-until", online.pass.until); else localStorage.removeItem("atlas-pass-until");
  renderPassStatus();
  if (atlas.classList.contains("is-visible")) renderAtlas();
}

async function api(path, options = {}) {
  const apiRoot = isNative ? `${SITE_URL}api` : "/api";
  const response = await fetch(`${apiRoot}${path}`, {
    credentials: isNative ? "include" : "same-origin",
    headers: { "Content-Type": "application/json", "Accept-Language": lang, ...options.headers },
    ...options
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || t("error.generic"));
  return data;
}

async function syncProgress() {
  if (!online.user) return;
  try {
    const { progress } = await api("/progress", { method: "PUT", body: JSON.stringify({ completed: state.completed, hints: state.hints }) });
    online.user.completed = progress.completed;
    online.user.hints = progress.hints;
  } catch (_) {}
}

function tone(frequency = 540, duration = .12, delay = 0) {
  if (state.muted) return;
  try {
    audioContext ||= new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0, audioContext.currentTime + delay);
    gain.gain.linearRampToValueAtTime(.055, audioContext.currentTime + delay + .015);
    gain.gain.exponentialRampToValueAtTime(.001, audioContext.currentTime + delay + duration);
    oscillator.connect(gain).connect(audioContext.destination);
    oscillator.start(audioContext.currentTime + delay);
    oscillator.stop(audioContext.currentTime + delay + duration);
  } catch (_) {}
}

function showToast(message) {
  $("#toast-text").textContent = message;
  $("#toast").classList.add("visible");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => $("#toast").classList.remove("visible"), 2600);
}

function setMuted(value, announce = true) {
  state.muted = value;
  document.body.classList.toggle("muted", value);
  $$(".sound-toggle").forEach(button => {
    button.setAttribute("aria-pressed", String(value));
    button.setAttribute("aria-label", value ? t("sound.on") : t("sound.off"));
  });
  save();
  if (!value && announce) tone(620, .12);
}

function updateBeginLabel() {
  const key = state.completed === levels.length ? "intro.museum" : state.completed > 0 ? "intro.continue" : "intro.begin";
  $("#begin-label").textContent = t(key);
}

// ---------- language ----------

function applyLanguage(next = lang) {
  lang = setLanguage(next);
  levels = localizedLevels(lang);
  expeditions = baseExpeditions.map(item => localizeExpedition(item, lang));
  document.title = "Atlas of Lost Art";
  applyStaticTranslations();
  setMuted(state.muted, false);
  updateBeginLabel();
  $("#pass-benefit1").textContent = t("pass.benefit1", { count: levels.length, expeditions: expeditions.length });
  if (atlas.classList.contains("is-visible")) renderAtlas();
  if (game.classList.contains("is-visible")) renderLevelCopy(true);
  if (reveal.classList.contains("open")) renderRevealCopy();
  if (collectionModal.classList.contains("open")) renderCollection();
  if (online.available || online.challenge) { renderChallenge(); renderLeaderboard(); }
  renderStreak();
  renderAccount();
  renderPassStatus();
}

function toggleLanguage() {
  applyLanguage(lang === "ru" ? "en" : "ru");
  tone(500, .1);
}

// ---------- views ----------

function showView(name) {
  intro.hidden = name !== "intro";
  intro.classList.toggle("is-leaving", name !== "intro");
  atlas.classList.toggle("is-visible", name === "atlas");
  atlas.setAttribute("aria-hidden", String(name !== "atlas"));
  game.classList.toggle("is-visible", name === "game");
  game.setAttribute("aria-hidden", String(name !== "game"));
  window.scrollTo(0, 0);
}

function enterGame() {
  tone(392, .18);
  tone(523, .28, .12);
  hapticTap(ImpactStyle.Medium);
  intro.classList.add("is-leaving");
  setTimeout(() => openAtlas(), 500);
}

function goHome() {
  closeModal();
  closeReveal();
  showView("intro");
  updateBeginLabel();
}

function openAtlas() {
  closeModal();
  closeReveal();
  state.mode = "campaign";
  state.attemptId = null;
  state.challengeSalt = null;
  if (state.atlasExpedition === null) state.atlasExpedition = getExpeditionForLevel(Math.min(state.completed, levels.length - 1), lang).id;
  showView("atlas");
  renderAtlas();
}

function startLevel(index) {
  if (levelLocked(index)) {
    openPass(index);
    return;
  }
  state.mode = "campaign";
  state.attemptId = null;
  state.challengeSalt = null;
  closeModal();
  closeReveal();
  showView("game");
  loadLevel(index);
}

// ---------- atlas ----------

function renderAtlas() {
  const activeId = state.atlasExpedition || expeditions[0].id;
  const expedition = expeditions.find(item => item.id === activeId) || expeditions[0];
  const stops = expeditionIndexes(expedition.id);
  const done = stops.filter(item => item.index < state.completed).length;
  $("#atlas-kicker").textContent = t("atlas.kicker", { number: expedition.number, done, total: stops.length });
  $("#atlas-map-canvas").innerHTML = renderMapSvg({
    levels, expeditions, completed: state.completed, activeExpedition: expedition.id,
    labels: { locked: t("aria.locked"), done: t("aria.done"), current: t("aria.current") }
  });
  $$(".atlas-pin").forEach(pin => {
    const open = () => selectStop(Number(pin.dataset.level));
    pin.addEventListener("click", open);
    pin.addEventListener("keydown", event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); } });
  });

  const next = stops.find(item => item.index >= state.completed) || stops[0];
  const replay = next.index < state.completed;
  const level = next.level;
  $("#atlas-stop").classList.toggle("replay", replay);
  $("#atlas-stop-image").src = level.imageThumb;
  $("#atlas-stop-kicker").textContent = replay ? t("atlas.replay", { city: level.city }) : t(next.index === 0 ? "atlas.nextFirst" : "atlas.next", { city: level.city });
  $("#atlas-stop-title").textContent = level.title;
  $("#atlas-stop-meta").textContent = `${level.type} · ${level.year}`;
  $("#atlas-stop").onclick = () => selectStop(next.index);
  const allDone = state.completed >= levels.length;
  const nextIndex = Math.min(state.completed, levels.length - 1);
  $("#atlas-continue-label").textContent = allDone ? t("atlas.complete") : levelLocked(nextIndex) ? t("pass.locked") : t(state.completed === 0 ? "atlas.begin" : "atlas.continue");
  $("#atlas-continue").onclick = () => (allDone ? openCollection() : startLevel(nextIndex));

  $("#atlas-expedition-list").innerHTML = expeditions.map((item, position) => {
    const itemStops = expeditionIndexes(item.id);
    const itemDone = itemStops.filter(stop => stop.index < state.completed).length;
    const locked = itemStops[0].index > state.completed;
    const needsPass = levelLocked(itemStops[0].index);
    const count = needsPass ? `<span class="lock"><svg viewBox="0 0 24 24"><rect x="4" y="10" width="16" height="11" rx="1"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>${t("pass.locked")}</span>` : t("atlas.stops", { done: itemDone, total: itemStops.length });
    return `<li class="${item.id === expedition.id ? "active" : ""}${locked ? " locked" : ""}"><button type="button" data-expedition="${item.id}"><span class="index">${item.number}</span><span class="name">${item.name}<small>${item.subtitle}</small></span><span class="count">${count}</span></button></li>`;
  }).join("");
  $$("#atlas-expedition-list button").forEach(button => button.addEventListener("click", () => {
    state.atlasExpedition = button.dataset.expedition;
    tone(420, .07);
    renderAtlas();
  }));
  renderAtlasDaily();
  renderStreak();
}

function renderAtlasDaily() {
  const challenge = online.challenge;
  const level = challenge ? levels.find(item => item.id === challenge.levelId) : null;
  $("#atlas-daily-image").src = level ? level.imageThumb : levels[0].imageThumb;
  $("#atlas-daily-image").style.filter = level ? "blur(4px) brightness(.7)" : "";
  $("#atlas-daily-title").textContent = level ? (challenge.best ? t("atlas.dailyDone", { rank: online.lastResult?.rank ?? "—" }) : level.title) : t("intro.dailyTitle");
  $("#atlas-daily-players").textContent = challenge ? formatNumber(challenge.players) : "—";
}

function selectStop(index) {
  if (index > state.completed) {
    showToast(t("atlas.lockedToast"));
    tone(180, .08);
    return;
  }
  hapticTap();
  startLevel(index);
}

function loadLevel(index) {
  state.level = index;
  state.history = [];
  state.solved = false;
  state.actions = 0;
  state.hintsUsed = 0;
  state.startedAt = performance.now();
  state.solvedMs = 0;
  state.events = [];
  state.selected = null;
  state.atlasExpedition = getExpeditionForLevel(index, lang).id;
  const level = current();
  track("level_start", { level: index + 1, mechanic: level.mechanic });
  $("#artifact-image").src = level.imageThumb;
  $("#hint-count").textContent = state.hints;
  $("#hint-button").disabled = state.hints <= 0;
  $$(".difficulty i").forEach((bar, position) => bar.classList.toggle("active", position < level.difficulty));
  $("#reset-button").disabled = state.mode === "daily";
  renderLevelCopy(false);
  resetPuzzle(false);
}

function renderLevelCopy(rerenderBoard) {
  const index = state.level;
  const level = current();
  $("#puzzle-number").textContent = state.mode === "daily" ? t("puzzle.ranked", { city: level.city }) : t("puzzle.chapter", { number: pad2(index + 1), city: level.city });
  $("#puzzle-title").textContent = level.title;
  $("#artifact-year").textContent = level.year;
  $("#artifact-type").textContent = level.type;
  $("#artifact-name").textContent = t("artifact.hidden");
  $("#artifact-clue").textContent = level.clue;
  $("#curator-note").textContent = level.note;
  $("#drag-hint-text").textContent = level.hintText;
  renderJourneyNavigation();
  updateProgress();
  if (rerenderBoard && board.children.length) renderPuzzle();
}

function renderJourneyNavigation() {
  const expedition = getExpeditionForLevel(state.level, lang);
  const items = expeditionIndexes(expedition.id);
  const route = $(".route-progress");
  const list = $(".journey-list");
  $("#journey-route").textContent = t("journey.route", { number: expedition.number });
  $("#journey-title").textContent = expedition.name;
  route.innerHTML = `<div class="route-progress__line"><i id="route-fill"></i></div>${items.map(({ level, index }) => `<button class="route-node" data-level="${index}" type="button"><i></i><span>${level.city}</span></button>`).join("")}`;
  list.innerHTML = items.map(({ level, index }, position) => `<li data-level-item="${index}"><span class="journey-list__index">${pad2(position + 1)}</span><i></i><div><b>${level.city}</b><small>${level.title}</small></div></li>`).join("");
  $$(".route-node").forEach(button => button.addEventListener("click", () => {
    const index = Number(button.dataset.level);
    if (state.mode === "campaign" && index <= state.completed) startLevel(index);
  }));
}

function updateProgress() {
  $("#found-count").textContent = state.completed;
  $(".discovery-count small").textContent = `/ ${levels.length}`;
  const items = expeditionIndexes(getExpeditionForLevel(state.level, lang).id);
  const localPosition = Math.max(0, items.findIndex(item => item.index === state.level));
  $("#route-fill").style.width = `${Math.min(100, localPosition / Math.max(1, items.length - 1) * 100)}%`;
  $$(".route-node").forEach(node => {
    const index = Number(node.dataset.level);
    node.classList.toggle("complete", index < state.completed);
    node.classList.toggle("active", index === state.level && state.completed < levels.length);
    node.disabled = index > state.completed;
    const status = index < state.completed ? t("aria.done") : index === state.level ? t("aria.current") : t("aria.locked");
    node.setAttribute("aria-label", t("aria.chapter", { city: levels[index].city, status }));
  });
  $$("[data-level-item]").forEach(item => {
    const index = Number(item.dataset.levelItem);
    item.classList.toggle("complete", index < state.completed);
    item.classList.toggle("active", index === state.level && state.completed < levels.length);
  });
}

// ---------- puzzle ----------

function resetPuzzle(withSound = true) {
  state.history = [];
  state.solved = false;
  state.selected = null;
  state.settledAt = [];
  state.values = initialState(current(), state.challengeSalt || state.mode);
  state.restoredMask = restorationMask(current(), state.values);
  renderPuzzle();
  if (withSound) {
    tone(310, .1);
    showToast(t("puzzle.resetToast"));
  }
}

function pieceClass(mask, previous, index) {
  if (mask[index]) return previous[index] ? " restored" : " restored healed";
  return " unrestored";
}

function renderRestoreMeter(mask) {
  const level = current();
  const count = restoredCount(level, state.values, mask);
  const total = pieceTotal(level);
  $("#restore-count").textContent = count;
  $("#restore-total").textContent = total;
  $("#restore-fill").style.width = `${count / total * 100}%`;
  $("#restore-meter").classList.toggle("complete", count === total);
}

function renderPuzzle() {
  const level = current();
  $("#undo-button").disabled = state.history.length === 0;
  const mask = restorationMask(level, state.values);
  const previous = state.restoredMask.length === mask.length ? state.restoredMask : mask;
  const renderers = { mosaic: renderMosaic, rings: renderRings, layers: renderLayers, swap: renderSwap, rotate: renderRotate, sequence: renderSequence };
  renderers[level.mechanic](mask, previous);
  const newlyHealed = mask.some((value, index) => value && !previous[index]);
  if (newlyHealed && !state.solved) tone(880, .14, .02);
  mask.forEach((value, index) => {
    if (!value) state.settledAt[index] = null;
    else if (state.settledAt[index] === null || state.settledAt[index] === undefined) state.settledAt[index] = state.actions;
  });
  state.restoredMask = mask;
  renderRestoreMeter(mask);
}

function commit(type, payload, sound) {
  if (state.solved) return;
  const level = current();
  let next;
  try { next = applyEvent(level, state.values, { type, payload }); }
  catch (_) { tone(180, .08); return; }
  state.history.push([...state.values]);
  state.values = next;
  state.selected = null;
  recordAction(type, payload);
  tone(sound, .08);
  hapticTap();
  renderPuzzle();
  checkSolved();
}

function recordAction(type, payload = {}) {
  state.actions++;
  state.events.push({
    seq: state.events.length + 1,
    type,
    payload,
    elapsedMs: Math.max(0, Math.round(performance.now() - state.startedAt))
  });
}

const gridPosition = (value, cols) => `${(value % cols) / Math.max(1, cols - 1) * 100}% ${Math.floor(value / cols) / Math.max(1, cols - 1) * 100}%`;

function gridBoard(className, cols) {
  board.className = `puzzle-board ${className}`;
  board.innerHTML = "";
  board.style.gridTemplateColumns = `repeat(${cols}, minmax(0, 1fr))`;
  board.style.gridTemplateRows = className === "sequence-board" ? "" : `repeat(${cols}, minmax(0, 1fr))`;
}

function renderMosaic(mask, previous) {
  const level = current();
  const cols = level.config.cols;
  const blank = blankIndex(level);
  gridBoard("mosaic-board", cols);
  state.values.forEach((value, position) => {
    const tile = document.createElement("button");
    tile.type = "button";
    if (value === blank) {
      tile.className = "mosaic-blank";
      tile.disabled = true;
      tile.setAttribute("aria-label", t("aria.blank"));
    } else {
      tile.className = `mosaic-tile${pieceClass(mask, previous, position)}`;
      tile.style.backgroundImage = `url("${level.imageMid}")`;
      tile.style.backgroundSize = `${cols * 100}% ${cols * 100}%`;
      tile.style.backgroundPosition = gridPosition(value, cols);
      tile.setAttribute("aria-label", t("aria.tile", { number: value + 1 }));
      tile.addEventListener("click", () => commit("slide", { position }, 330 + position * 9));
    }
    board.append(tile);
  });
}

function pairSelect(position, type, sound) {
  if (state.solved) return;
  if (state.selected === null) {
    state.selected = position;
    tone(360, .07);
    renderPuzzle();
    return;
  }
  if (state.selected === position) {
    state.selected = null;
    renderPuzzle();
    return;
  }
  const first = state.selected;
  commit(type, { first, second: position }, sound);
}

function renderSwap(mask, previous) {
  const level = current();
  const cols = level.config.cols;
  gridBoard("swap-board", cols);
  state.values.forEach((value, position) => {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = `swap-tile${state.selected === position ? " selected" : ""}${pieceClass(mask, previous, position)}`;
    tile.style.backgroundImage = `url("${level.imageMid}")`;
    tile.style.backgroundSize = `${cols * 100}% ${cols * 100}%`;
    tile.style.backgroundPosition = gridPosition(value, cols);
    tile.setAttribute("aria-label", state.selected === position ? t("aria.tileSelected", { number: position + 1 }) : t("aria.selectTile", { number: position + 1 }));
    tile.addEventListener("click", () => pairSelect(position, "swap", 470));
    board.append(tile);
  });
}

function renderRotate(mask, previous) {
  const level = current();
  const cols = level.config.cols;
  gridBoard("rotate-board", cols);
  state.values.forEach((rotation, position) => {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = `rotate-tile${pieceClass(mask, previous, position)}`;
    tile.setAttribute("aria-label", t("aria.rotateTile", { number: position + 1, degrees: rotation % 360 }));
    const image = document.createElement("span");
    image.style.backgroundImage = `url("${level.imageMid}")`;
    image.style.backgroundSize = `${cols * 100}% ${cols * 100}%`;
    image.style.backgroundPosition = gridPosition(position, cols);
    image.style.transform = `rotate(${rotation}deg) scale(1.04)`;
    tile.append(image);
    tile.addEventListener("click", () => commit("rotate", { position }, 380 + position * 8));
    board.append(tile);
  });
}

function renderSequence(mask, previous) {
  const level = current();
  const strips = level.config.strips;
  gridBoard("sequence-board", strips);
  state.values.forEach((value, position) => {
    const panel = document.createElement("button");
    panel.type = "button";
    panel.className = `sequence-panel${state.selected === position ? " selected" : ""}${pieceClass(mask, previous, position)}`;
    panel.style.backgroundImage = `url("${level.imageMid}")`;
    panel.style.backgroundSize = `${strips * 100}% 100%`;
    panel.style.backgroundPosition = `${value / (strips - 1) * 100}% 50%`;
    panel.setAttribute("aria-label", state.selected === position ? t("aria.stripSelected", { number: position + 1 }) : t("aria.strip", { number: position + 1 }));
    panel.addEventListener("click", () => pairSelect(position, "sequence", 485));
    board.append(panel);
  });
}

function renderRings(mask, previous) {
  const level = current();
  const count = level.config.count;
  board.className = "puzzle-board rings-board";
  board.innerHTML = "";
  board.style.gridTemplateColumns = "";
  board.style.gridTemplateRows = "";
  const labels = ["aria.ringOuter", "aria.ringMiddle", "aria.ringInner"];
  state.values.forEach((rotation, index) => {
    const inset = index * 50 / count;
    const scale = 100 / (100 - 2 * inset) * 100;
    const ring = document.createElement("button");
    ring.type = "button";
    ring.className = `ring${pieceClass(mask, previous, index)}`;
    ring.style.inset = `${inset}%`;
    ring.setAttribute("aria-label", t(index === 0 ? labels[0] : index === count - 1 ? labels[2] : labels[1]));
    const image = document.createElement("img");
    image.src = level.imageMid;
    image.alt = "";
    image.style.width = `${scale}%`;
    image.style.height = `${scale}%`;
    image.style.inset = `${-(scale - 100) / 2}%`;
    image.style.transform = `rotate(${rotation}deg)`;
    ring.append(image);
    ring.addEventListener("click", event => { event.stopPropagation(); commit("ring", { index }, 280 + index * 70); });
    board.append(ring);
  });
  board.insertAdjacentHTML("beforeend", '<span class="ring-center" aria-hidden="true"></span>');
}

function renderLayers(mask, previous) {
  const level = current();
  const bands = level.config.bands;
  board.className = "puzzle-board layers-board";
  board.innerHTML = "";
  board.style.gridTemplateColumns = "";
  board.style.gridTemplateRows = "";
  state.values.forEach((offset, index) => {
    const band = document.createElement("div");
    band.className = `layer-band${pieceClass(mask, previous, index)}`;
    band.tabIndex = 0;
    band.setAttribute("role", "group");
    band.setAttribute("aria-label", t("aria.layer", { number: index + 1, offset }));
    band.innerHTML = `<img src="${level.imageMid}" alt="" style="height: ${bands * 100}%; top: ${-index * 100}%; transform: translateX(${offset}px)"><div class="layer-controls"><button type="button" data-direction="-1" aria-label="${t("aria.layerLeft", { number: index + 1 })}"><svg viewBox="0 0 24 24"><path d="m15 6-6 6 6 6"/></svg></button><button type="button" data-direction="1" aria-label="${t("aria.layerRight", { number: index + 1 })}"><svg viewBox="0 0 24 24"><path d="m9 6 6 6-6 6"/></svg></button></div>`;
    $$("button", band).forEach(button => button.addEventListener("click", event => {
      event.stopPropagation();
      commit("layer-shift", { index, amount: Number(button.dataset.direction) * LAYER_STEP }, 350 + index * 30);
    }));
    let originX = 0;
    let originOffset = 0;
    let dragOffset = offset;
    const onMove = event => {
      if (!originX) return;
      dragOffset = Math.max(-LAYER_LIMIT, Math.min(LAYER_LIMIT, originOffset + event.clientX - originX));
      $("img", band).style.transform = `translateX(${dragOffset}px)`;
    };
    const onUp = () => {
      if (!originX) return;
      originX = 0;
      const snapped = Math.round(dragOffset / LAYER_STEP) * LAYER_STEP;
      if (snapped === state.values[index]) { renderPuzzle(); return; }
      commit("layer", { index, offset: snapped }, 350 + index * 30);
    };
    band.addEventListener("pointerdown", event => {
      if (event.target.closest("button") || state.solved) return;
      originX = event.clientX;
      originOffset = state.values[index];
      band.setPointerCapture(event.pointerId);
    });
    band.addEventListener("pointermove", onMove);
    band.addEventListener("pointerup", onUp);
    band.addEventListener("pointercancel", onUp);
    band.addEventListener("keydown", event => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") commit("layer-shift", { index, amount: event.key === "ArrowLeft" ? -LAYER_STEP : LAYER_STEP }, 350 + index * 30);
    });
    board.append(band);
  });
}

function checkSolved() {
  if (state.solved || !isSolved(current(), state.values)) return;
  state.solved = true;
  state.solvedMs = performance.now() - state.startedAt;
  hapticSuccess();
  tone(523, .2);
  tone(659, .22, .13);
  tone(784, .35, .26);
  setTimeout(openReveal, 900);
}

function useHint() {
  if (state.hints <= 0 || state.solved) return;
  const level = current();
  const target = hintTarget(level, state.values);
  if (!target) return;
  state.hints--;
  state.hintsUsed++;
  recordAction("hint");
  save();
  $("#hint-count").textContent = state.hints;
  $("#hint-button").disabled = state.hints <= 0;
  const pieces = $$(".puzzle-board > *").filter(node => !node.classList.contains("ring-center"));
  const elements = [pieces[target.index], target.partner !== undefined ? pieces[target.partner] : null].filter(Boolean);
  const className = level.mechanic === "mosaic" ? "movable" : "hint";
  const message = level.mechanic === "rings" ? t("hint.ring")
    : level.mechanic === "layers" ? t("hint.layer", { direction: t(state.values[target.index] > 0 ? "hint.left" : "hint.right") })
    : level.mechanic === "swap" || level.mechanic === "sequence" ? t("hint.pair")
    : level.mechanic === "rotate" ? t("hint.rotate") : t("hint.mosaic");
  elements.forEach(element => element.classList.add(className));
  showToast(message);
  setTimeout(() => elements.forEach(element => element.classList.remove(className)), 1700);
  tone(690, .18);
}

function undo() {
  if (!state.history.length || state.solved) return;
  state.values = state.history.pop();
  state.selected = null;
  recordAction("undo");
  tone(260, .08);
  renderPuzzle();
}

// ---------- reveal ----------

function renderRevealCopy() {
  const level = current();
  $("#discovery-image").alt = level.name;
  $("#discovery-index").textContent = `${pad2(state.level + 1)} / ${pad2(levels.length)}`;
  $("#discovery-title").textContent = level.name;
  $("#discovery-artist").textContent = level.artist;
  $("#discovery-story").textContent = level.story;
  $("#reveal-time").textContent = formatTime(state.solvedMs);
  $("#reveal-hints").textContent = hintsLabel(state.hintsUsed);
  $("#reveal-moves").textContent = t("reveal.moves", { count: state.actions - state.hintsUsed });
  const nextKey = state.mode === "daily" ? "reveal.submit" : state.level === levels.length - 1 ? "reveal.openMuseum" : "reveal.hang";
  $("#next-button span").textContent = t(nextKey);
}

function openReveal() {
  $("#discovery-image").src = current().image;
  renderRevealCopy();
  $("#next-button").disabled = false;
  reveal.classList.remove("open");
  void reveal.offsetWidth;
  reveal.classList.add("open");
  reveal.setAttribute("aria-hidden", "false");
  document.body.classList.add("reveal-open");
  $("#next-button").focus();
}

function closeReveal() {
  reveal.classList.remove("open");
  reveal.setAttribute("aria-hidden", "true");
  document.body.classList.remove("reveal-open");
}

async function acceptDiscovery() {
  if (state.mode === "daily") {
    const button = $("#next-button");
    button.disabled = true;
    button.querySelector("span").textContent = t("reveal.checking");
    try {
      const { result, streak } = await api(`/attempts/${state.attemptId}/finish`, { method: "POST", body: JSON.stringify({ events: state.events }) });
      online.lastResult = result;
      track("daily_complete", { level: state.level + 1, mechanic: current().mechanic });
      if (streak) online.streak = streak;
      await Promise.all([loadChallenge(), loadLeaderboard()]);
      closeReveal();
      showView("atlas");
      renderAtlas();
      openArena();
      showToast(result.players ? t("arena.resultPlayers", { score: formatNumber(result.score), rank: result.rank, players: result.players }) : t("arena.resultToast", { score: formatNumber(result.score), rank: result.rank }));
    } catch (error) {
      showToast(error.message);
      button.disabled = false;
      button.querySelector("span").textContent = t("reveal.retry");
    }
    return;
  }
  const finished = state.level;
  track("level_complete", { level: finished + 1, mechanic: current().mechanic });
  state.completed = Math.max(state.completed, finished + 1);
  save();
  updateProgress();
  closeReveal();
  const expeditionDone = expeditionIndexes(levels[finished].expedition).every(item => item.index < state.completed);
  if (state.completed >= levels.length) {
    openCollection();
  } else if (expeditionDone || finished + 1 !== state.completed || levelLocked(state.completed)) {
    openAtlas();
  } else {
    loadLevel(state.completed);
  }
}

function shareTexts() {
  const level = current();
  return {
    text: t("reveal.shareText", { name: level.name, artist: level.artist, time: formatTime(state.solvedMs), hints: hintsLabel(state.hintsUsed) }),
    url: SITE_URL
  };
}

async function shareText(text, url, copiedKey) {
  try {
    if (navigator.share) {
      await navigator.share({ title: "Atlas of Lost Art", text, url });
      return;
    }
    await navigator.clipboard.writeText(`${text} ${url}`);
    showToast(t(copiedKey));
  } catch (error) {
    if (error?.name !== "AbortError") showToast(t(copiedKey));
  }
}

function shareDiscovery() {
  const { text, url } = shareTexts();
  tone(520, .1);
  shareText(text, url, "reveal.shareCopied");
}

async function shareCard() {
  const button = $("#share-card-button");
  button.disabled = true;
  tone(520, .1);
  try {
    const level = current();
    const total = Math.max(1, state.actions);
    const settled = state.settledAt.map(value => (value === null || value === undefined ? null : Math.min(1, value / total)));
    if (level.mechanic === "mosaic") settled[state.values.indexOf(blankIndex(level))] = null;
    const daily = state.mode === "daily" && online.challenge;
    const streak = online.streak?.current || 0;
    const blob = await renderShareCard({
      mechanic: level.mechanic,
      config: level.config,
      settled,
      texts: {
        day: daily ? t("share.day", { day: dailyNumber(online.challenge.day) }) : t("share.campaign", { number: pad2(state.level + 1) }),
        kicker: daily ? t("share.kicker") : t("share.kickerCampaign"),
        time: formatTime(state.solvedMs),
        hints: hintsLabel(state.hintsUsed),
        moves: t("reveal.moves", { count: state.actions - state.hintsUsed }),
        rank: online.lastResult?.rank && online.lastResult?.players ? t("share.rank", { rank: online.lastResult.rank, players: online.lastResult.players }) : "",
        legendFast: t("share.legendFast"), legendMid: t("share.legendMid"), legendSlow: t("share.legendSlow"),
        streak: streak > 0 ? t("share.streak", { count: streak }) : t("share.noStreak"),
        museum: t("share.museum", { count: Math.max(state.completed, state.mode === "campaign" ? state.level + 1 : 0), total: levels.length }),
        tease: t("share.tease")
      }
    });
    const file = new File([blob], t("share.file"), { type: "image/png" });
    const { text, url } = shareTexts();
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: "Atlas of Lost Art", text: `${text} ${url}` });
      showToast(t("share.cardReady"));
      return;
    }
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = t("share.file");
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 4000);
    showToast(t("share.cardSaved"));
  } catch (error) {
    if (error?.name !== "AbortError") showToast(error.message || t("error.generic"));
  } finally { button.disabled = false; }
}

// ---------- museum ----------

function openCollection() {
  renderCollection();
  if (!game.classList.contains("is-visible") && !atlas.classList.contains("is-visible")) openAtlas();
  modalBackdrop.classList.add("open");
  modalBackdrop.setAttribute("aria-hidden", "false");
  arenaModal.classList.remove("open");
  passModal.classList.remove("open");
  collectionModal.classList.add("open");
  $("#collection-close").focus();
}

function renderCollection() {
  $("#collection-count").textContent = state.completed;
  $("#collection-total").textContent = levels.length;
  $("#museum-halls").innerHTML = halls.map(hall => {
    const items = levels.map((level, index) => ({ level, index })).filter(item => item.level.hall === hall);
    const found = items.filter(item => item.index < state.completed).length;
    return `<button type="button" role="tab" aria-selected="${hall === state.hall}" class="${hall === state.hall ? "on" : ""}" data-hall="${hall}">${t(`hall.${hall}`)}<b>${found}/${items.length}</b></button>`;
  }).join("");
  $$("#museum-halls button").forEach(button => button.addEventListener("click", () => {
    state.hall = button.dataset.hall;
    tone(420, .07);
    renderCollection();
  }));
  const items = levels.map((level, index) => ({ level, index })).filter(item => item.level.hall === state.hall);
  $("#collection-grid").innerHTML = items.map(({ level, index }) => {
    const found = index < state.completed;
    const plaque = found ? `${level.artist.split(" · ")[0]} · ${level.year}` : t("museum.empty");
    return `
    <button type="button" data-collection-level="${index}" class="collection-card ${found ? "" : "locked"}" ${found ? "" : "disabled"}>
      <div class="collection-card__image"><img src="${level.imageThumb}" loading="lazy" alt="${found ? level.name : t("collection.unknownAlt")}"></div>
      <small>${plaque}</small>
      <h3>${found ? level.name : t("collection.unknown")}</h3>
    </button>`;
  }).join("");
  $$("[data-collection-level]").forEach(card => card.addEventListener("click", () => startLevel(Number(card.dataset.collectionLevel))));
  $("#museum-pass-button").hidden = hasPass();
}

function shareMuseum() {
  tone(520, .1);
  shareText(t("museum.shareText", { count: state.completed, total: levels.length }), SITE_URL, "museum.shareCopied");
}

function closeModal() {
  modalBackdrop.classList.remove("open");
  modalBackdrop.setAttribute("aria-hidden", "true");
  collectionModal.classList.remove("open");
  arenaModal.classList.remove("open");
  passModal.classList.remove("open");
}

function restart() {
  state.completed = 0;
  state.hints = 12;
  state.atlasExpedition = expeditions[0].id;
  save();
  startLevel(0);
  showToast(t("collection.restartToast"));
}

// ---------- museum pass ----------

function openPass(lockedIndex = null) {
  track("pass_open", { level: lockedIndex === null ? undefined : lockedIndex + 1 });
  const expedition = lockedIndex === null ? null : getExpeditionForLevel(lockedIndex, lang);
  $("#pass-kicker").textContent = expedition ? t("pass.kicker", { number: expedition.number }) : t("pass.locked");
  renderPassStatus();
  modalBackdrop.classList.add("open");
  modalBackdrop.setAttribute("aria-hidden", "false");
  collectionModal.classList.remove("open");
  arenaModal.classList.remove("open");
  passModal.classList.add("open");
  $("#pass-close").focus();
}

function renderPassStatus() {
  const status = $("#pass-status");
  const active = hasPass();
  status.hidden = !active;
  if (active) {
    const until = new Date(online.pass.until);
    const forever = online.pass.permanent || until.getTime() - Date.now() > 1000 * 60 * 60 * 24 * 365 * 20;
    status.textContent = forever ? t("pass.activeForever") : t("pass.active", { date: until.toLocaleDateString(locale(), { year: "numeric", month: "long", day: "numeric" }) });
  }
  $("#pass-form").hidden = active;
  $("#pass-store").hidden = active;
  $("#museum-pass-button").hidden = active;
}

async function redeemPass(event) {
  event.preventDefault();
  if (!$("#pass-form").reportValidity()) return;
  const button = $("#pass-redeem");
  button.disabled = true;
  try {
    const { pass } = await api("/pass/redeem", { method: "POST", body: JSON.stringify({ code: $("#pass-code").value }) });
    setPass(pass);
    $("#pass-code").value = "";
    tone(523, .2); tone(659, .22, .13);
    showToast(t("pass.redeemed"));
  } catch (error) { showToast(error.message); }
  finally { button.disabled = false; }
}

async function restorePass() {
  try {
    const { pass } = await api("/me");
    setPass(pass);
    showToast(hasPass() ? t("pass.redeemed") : t("pass.restored"));
  } catch (error) { showToast(error.message); }
}

// ---------- online ----------

const guestLabel = () => online.guest?.handle || online.guest?.label || "";

function renderAccount() {
  const user = online.user;
  $("#auth-form").hidden = Boolean(user);
  $("#profile-card").hidden = !user;
  $$(".account-pill b").forEach(node => { node.textContent = user ? user.handle : online.guest?.handle || t("account.signin"); });
  if (user) {
    $("#profile-avatar").textContent = user.handle.slice(0, 1).toLocaleUpperCase(locale());
    $("#profile-handle").textContent = user.handle;
    const result = online.challenge?.best ? t("account.todayScore", { score: formatNumber(online.challenge.best) }) : t("account.noResult");
    $("#profile-stats").textContent = t("account.stats", { count: state.completed, result });
    $("#account-title").textContent = t("account.titleSigned");
    $("#account-copy").textContent = t("account.copySigned");
  } else {
    $("#account-title").textContent = t("account.title");
    $("#account-copy").textContent = t("account.copy");
  }
  renderGuestForm();
}

function renderGuestForm() {
  const form = $("#guest-form");
  const show = !online.user && online.guest && !online.guest.handle && Boolean(online.challenge?.best);
  form.hidden = !show;
  if (show) $("#guest-copy").textContent = t("guest.playAs", { label: online.guest.label });
}

function renderStreak() {
  const streak = online.streak;
  const currentStreak = streak?.current || 0;
  const pill = $("#streak-pill");
  pill.classList.toggle("empty", currentStreak === 0);
  $("#streak-count").textContent = currentStreak;
  pill.setAttribute("aria-label", currentStreak ? t("atlas.streak", { count: currentStreak }) : t("atlas.noStreak"));
  $("#streak-title").textContent = currentStreak ? t("streak.title", { count: currentStreak }) : t("streak.none");
  $("#streak-best").textContent = streak?.best ? t("streak.best", { count: streak.best }) : "";
  const week = streak?.week || [false, false, false, false, false, false, false];
  const today = new Date();
  const formatter = new Intl.DateTimeFormat(locale(), { weekday: "short", timeZone: "UTC" });
  $("#streak-week").innerHTML = week.map((on, index) => {
    const date = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - (6 - index)));
    return `<span class="${on ? "on" : ""}${index === 6 ? " today" : ""}">${formatter.format(date).replace(".", "").slice(0, 2)}</span>`;
  }).join("");
}

function renderChallenge() {
  const challenge = online.challenge;
  if (!challenge) {
    $("#daily-status").textContent = online.available ? t("arena.updating") : t("arena.offline");
    $("#daily-start").disabled = true;
    $("#daily-start span").textContent = t("arena.start");
    $("#daily-work").textContent = t("arena.loading");
    $("#daily-mechanic").textContent = t("arena.unknownPuzzle");
    $("#daily-location").textContent = t("arena.today");
    $("#daily-title").textContent = t("intro.dailyTitle");
    renderAtlasDaily();
    return;
  }
  const level = levels.find(item => item.id === challenge.levelId);
  $("#daily-image").src = level.imageThumb;
  $("#daily-image").alt = level.name;
  $("#daily-work").textContent = level.name;
  $("#daily-location").textContent = t("arena.location", { city: level.city });
  $("#daily-mechanic").textContent = t("arena.mechanic", { mechanic: t(`mechanic.${level.mechanic}`), difficulty: level.difficulty });
  $("#daily-title").textContent = level.title;
  $("#daily-players").textContent = formatNumber(challenge.players);
  const remaining = challenge.attemptsRemaining;
  const identified = Boolean(online.user || online.guest);
  $("#daily-start span").textContent = remaining <= 0 ? t("arena.exhausted") : identified && challenge.attemptsUsed > 0 ? t("arena.startRemaining", { remaining }) : online.user ? t("arena.start") : t("arena.startGuest");
  $("#daily-start").disabled = remaining <= 0;
  $("#daily-status").textContent = online.lastResult
    ? (online.lastResult.players ? t("arena.resultPlayers", { score: formatNumber(online.lastResult.score), rank: online.lastResult.rank, players: online.lastResult.players }) : t("arena.result", { score: formatNumber(online.lastResult.score), rank: online.lastResult.rank }))
    : challenge.best
      ? t("arena.best", { score: formatNumber(challenge.best) })
      : online.user ? t("arena.verifiedByServer") : online.guest ? t("arena.guestStatus", { label: guestLabel() }) : t("arena.needProfile");
  updateCountdown();
  renderAtlasDaily();
}

function updateCountdown() {
  const endsAt = online.challenge?.endsAt;
  if (!endsAt) return;
  const remaining = Math.max(0, new Date(endsAt).getTime() - Date.now());
  const hours = Math.floor(remaining / 3600000);
  const minutes = Math.floor(remaining % 3600000 / 60000);
  const seconds = Math.floor(remaining % 60000 / 1000);
  $("#daily-countdown").textContent = [hours, minutes, seconds].map(pad2).join(":");
}

function renderLeaderboard() {
  const list = $("#leaderboard");
  list.innerHTML = "";
  if (!online.leaderboard.length) {
    const item = document.createElement("li");
    item.className = "leaderboard-empty";
    item.textContent = online.available ? t("arena.leaderboardEmpty") : t("arena.leaderboardOffline");
    list.append(item);
    return;
  }
  online.leaderboard.forEach(entry => {
    const item = document.createElement("li");
    if (entry.isMe) item.classList.add("me");
    if (entry.guest) item.classList.add("guest");
    const rank = document.createElement("span");
    rank.className = "leaderboard-rank";
    rank.textContent = pad2(entry.rank);
    const handle = document.createElement("b");
    handle.textContent = entry.handle;
    const score = document.createElement("span");
    score.className = "leaderboard-score";
    score.textContent = formatNumber(entry.score);
    item.append(rank, handle, score);
    list.append(item);
  });
}

async function loadChallenge() {
  try {
    online.challenge = await api("/challenge");
    if (online.challenge.streak) online.streak = online.challenge.streak;
    online.available = true;
  } catch (_) {
    online.available = false;
    online.challenge = null;
  }
  renderChallenge();
  renderStreak();
  renderAccount();
}

async function loadLeaderboard() {
  try {
    const data = await api("/leaderboard");
    online.leaderboard = data.entries;
    online.available = true;
  } catch (_) { online.leaderboard = []; }
  renderLeaderboard();
}

async function initializeOnline() {
  try {
    const { user, guest, pass } = await api("/me");
    online.user = user;
    online.guest = guest;
    if (pass) setPass(pass);
    if (user) {
      state.completed = Math.max(state.completed, user.completed);
      state.hints = Math.max(state.hints, user.hints);
      save();
    }
    online.available = true;
  } catch (_) { online.available = false; }
  renderAccount();
  renderPassStatus();
  await Promise.all([loadChallenge(), loadLeaderboard()]);
}

function openArena() {
  modalBackdrop.classList.add("open");
  modalBackdrop.setAttribute("aria-hidden", "false");
  collectionModal.classList.remove("open");
  passModal.classList.remove("open");
  arenaModal.classList.add("open");
  renderChallenge();
  renderStreak();
  renderAccount();
  $("#arena-close").focus();
}

async function authenticate(mode) {
  const handle = $("#auth-handle").value;
  const password = $("#auth-password").value;
  if (!$("#auth-form").reportValidity()) return;
  const buttons = [$("#login-button"), $("#register-button")];
  buttons.forEach(button => button.disabled = true);
  try {
    const { user, pass } = await api(`/auth/${mode}`, { method: "POST", body: JSON.stringify({ handle, password, completed: state.completed, hints: state.hints }) });
    online.user = user;
    online.guest = null;
    if (pass) setPass(pass);
    state.completed = Math.max(state.completed, user.completed);
    state.hints = Math.max(state.hints, user.hints);
    save();
    $("#auth-password").value = "";
    await Promise.all([loadChallenge(), loadLeaderboard(), syncProgress()]);
    if (atlas.classList.contains("is-visible")) renderAtlas();
    showToast(t(mode === "register" ? "account.registered" : "account.welcome"));
    if (mode === "register") track("account_created");
  } catch (error) { showToast(error.message); }
  finally { buttons.forEach(button => button.disabled = false); }
}

async function saveGuestHandle(event) {
  event.preventDefault();
  if (!$("#guest-form").reportValidity()) return;
  const button = $("#guest-save");
  button.disabled = true;
  try {
    const { guest } = await api("/guest/handle", { method: "PUT", body: JSON.stringify({ handle: $("#guest-handle").value }) });
    online.guest = guest;
    await loadLeaderboard();
    renderAccount();
    showToast(t("guest.saved"));
  } catch (error) { showToast(error.message); }
  finally { button.disabled = false; }
}

async function startDaily() {
  const button = $("#daily-start");
  button.disabled = true;
  try {
    const attempt = await api("/attempts/start", { method: "POST", body: "{}" });
    if (attempt.guest) online.guest = attempt.guest;
    const index = levels.findIndex(level => level.id === attempt.levelId);
    if (index < 0) throw new Error(t("arena.levelMissing"));
    state.mode = "daily";
    state.attemptId = attempt.attemptId;
    state.challengeSalt = attempt.seed;
    state.level = index;
    closeModal();
    showView("game");
    loadLevel(index);
    showToast(t("arena.startedToast", { remaining: attempt.attemptsRemaining }));
  } catch (error) {
    showToast(error.message);
    await loadChallenge();
  } finally { button.disabled = false; }
}

// ---------- wiring ----------

$("#begin-button").addEventListener("click", enterGame);
$("#details-button").addEventListener("click", () => {
  showToast(t("intro.howToast"));
  tone(440, .1);
});
$("#daily-button").addEventListener("click", openArena);
$("#atlas-daily").addEventListener("click", openArena);
$("#account-button").addEventListener("click", openArena);
$("#atlas-account-button").addEventListener("click", openArena);
$("#streak-pill").addEventListener("click", openArena);
$("#arena-header-button").addEventListener("click", openArena);
$("#nav-museum").addEventListener("click", openCollection);
$("#nav-ranking").addEventListener("click", openArena);
$("#atlas-home").addEventListener("click", goHome);
$("#arena-close").addEventListener("click", closeModal);
$("#pass-close").addEventListener("click", closeModal);
$("#pass-form").addEventListener("submit", redeemPass);
$("#pass-restore").addEventListener("click", restorePass);
$("#museum-pass-button").addEventListener("click", () => openPass());
$("#museum-share").addEventListener("click", shareMuseum);
$("#daily-start").addEventListener("click", startDaily);
$("#guest-form").addEventListener("submit", saveGuestHandle);
$("#leaderboard-refresh").addEventListener("click", loadLeaderboard);
$("#auth-form").addEventListener("submit", event => { event.preventDefault(); authenticate("login"); });
$("#register-button").addEventListener("click", () => authenticate("register"));
$("#analytics-accept").addEventListener("click", () => chooseAnalytics("accepted"));
$("#analytics-decline").addEventListener("click", () => chooseAnalytics("declined"));
$("#analytics-settings").addEventListener("click", () => renderConsent(true));
$("#logout-button").addEventListener("click", async () => {
  try {
    await api("/auth/logout", { method: "POST", body: "{}" });
    online.user = null;
    online.lastResult = null;
    const { guest, pass } = await api("/me").catch(() => ({ guest: null, pass: null }));
    online.guest = guest;
    setPass(pass);
    await Promise.all([loadChallenge(), loadLeaderboard()]);
    showToast(t("account.loggedOut"));
  } catch (error) { showToast(error.message); }
});
$("#delete-account-button").addEventListener("click", async () => {
  if (!confirm(t("account.deleteConfirm"))) return;
  const password = prompt(t("account.deletePrompt"));
  if (!password) return;
  try {
    await api("/account", { method: "DELETE", body: JSON.stringify({ password }) });
    online.user = null;
    online.guest = null;
    online.lastResult = null;
    online.streak = null;
    setPass(null);
    await Promise.all([loadChallenge(), loadLeaderboard()]);
    showToast(t("account.deleted"));
  } catch (error) { showToast(error.message); }
});
$("#home-button").addEventListener("click", openAtlas);
$("#undo-button").addEventListener("click", undo);
$("#reset-button").addEventListener("click", () => resetPuzzle(true));
$("#hint-button").addEventListener("click", useHint);
$("#next-button").addEventListener("click", acceptDiscovery);
$("#share-button").addEventListener("click", shareDiscovery);
$("#share-card-button").addEventListener("click", shareCard);
$("#collection-button").addEventListener("click", openCollection);
$("#collection-close").addEventListener("click", closeModal);
$("#restart-button").addEventListener("click", restart);
$("#lang-toggle").addEventListener("click", toggleLanguage);
$("#lang-toggle-collection").addEventListener("click", toggleLanguage);
$$(".sound-toggle").forEach(button => button.addEventListener("click", () => setMuted(!state.muted)));
modalBackdrop.addEventListener("click", event => {
  if (event.target === modalBackdrop) closeModal();
});
document.addEventListener("keydown", event => {
  if (event.key === "Escape" && modalBackdrop.classList.contains("open")) closeModal();
});

async function initializePlatform() {
  document.body.classList.toggle("native", isNative);

  if (!isNative) {
    registerSW({
      immediate: true,
      onOfflineReady: () => showToast(t("sw.offline")),
      onNeedRefresh: () => showToast(t("sw.update"))
    });
    return;
  }

  try {
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch (_) {}

  NativeApp.addListener("appStateChange", ({ isActive }) => {
    if (!isActive) save();
  });

  NativeApp.addListener("backButton", () => {
    if (reveal.classList.contains("open")) {
      return;
    } else if (modalBackdrop.classList.contains("open")) {
      closeModal();
    } else if (game.classList.contains("is-visible")) {
      openAtlas();
    } else if (atlas.classList.contains("is-visible")) {
      goHome();
    } else {
      NativeApp.exitApp();
    }
  });

  window.addEventListener("load", async () => {
    try {
      await document.fonts.ready;
      await SplashScreen.hide({ fadeOutDuration: 300 });
    } catch (_) {}
  }, { once: true });
}

applyLanguage(lang);
updateProgress();
initializePlatform();
initializeOnline();
renderConsent();
setInterval(updateCountdown, 1000);
